/*
 * Module for generating intent examples from a custom utterance
 */

define([
  "marionette",
  "backbone",
  "jquery",
  "messaging/messenger",
  "configuration/properties",
  "modules/topicsSplit/topic/topicExamples/topicExamplesModel",
  "modules/topicsSplit/topic/topicExampleGeneratorModal/topicExampleGeneratorModalView",
  "modules/topicsSplit/topic/topicExampleGeneratorModal/topicExampleGeneratedList/topicExampleGeneratedListModule",
  "modules/topicsSplit/topic/topicExampleModal/exampleLocale/exampleLocaleModule",
  "modules/topicsSplit/topic/topicExampleModal/exampleEditor/exampleEditorModule",
  "modules/topicsSplit/topic/topicExampleModal/strengthGauge/strengthGaugeModule",
], function (
  Marionette,
  Backbone,
  $,
  Messenger,
  Properties,
  TopicExamplesModel,
  TopicExampleGeneratorModalView,
  TopicExampleGeneratedListModule,
  ExampleLocaleModule,
  ExampleEditorModule,
  StrengthGaugeModule,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.region = options.region;
      this.name = "topicExampleGeneratorModal";
      this.selectedLocale = options.localeId;
      this.topicId = options.topicId;
    },
    // pass an optional oldest authored example from which to generate examples.
    // This example can be edited to use a custom utterance for generation.
    show: function (exampleBundle) {
      // this modal is not meant for modifying the original example. Example model is only
      // used here to take advantage of strength gauge feature.
      if (!exampleBundle.example) {
        this.example = new TopicExamplesModel.topicExample();
        //set entries variable and set empty entry
        this.example.entries().addEntry(0);
      } else {
        this.example = exampleBundle.example.clone();
      }
      // use the original collection since we need to modify it to add new examples
      this.examples = exampleBundle.examplesCollection;
      // make sure the selected locale is set in the example model.
      this.example.set("localeId", this.selectedLocale);
      this.exampleGenerationModel = new TopicExamplesModel.generateExamples();
      this._updateExampleTextCollectionArray();
      this.layout = new TopicExampleGeneratorModalView.topicExampleGeneratorModalLayout({});
      this.locale = this.addSubModule(
        new ExampleLocaleModule({
          region: this.layout.locale,
          selectedLocale: this.selectedLocale,
          showOnlyEnglishLocales: true,
        }),
      );
      this.editor = this.addSubModule(new ExampleEditorModule({ region: this.layout.editor }));
      this.gauge = this.addSubModule(new StrengthGaugeModule({ region: this.layout.gauge }));
      this.generatedExamplesList = this.addSubModule(
        new TopicExampleGeneratedListModule({ region: this.layout.generatedExamplesList }),
      );
      this.layout.on(
        "generate:examples",
        function () {
          this._generateAndShowExamples();
        },
        this,
      );

      this.layout.on(
        "add:generated:examples",
        function () {
          // disable add example and regenerate example buttons while saving examples
          this.layout.disableButtons();
          // add the user selected examples to the topic
          const selectedExampleTexts = this.generatedExamplesList.getSelectedExampleTexts();
          if (selectedExampleTexts.length === 0) {
            Messenger.okDialog($.t("topic.examples.generator.noExamplesSelectedError"));
          }
          // create a new example in the collection for each selected example text
          // in the selected locale and immediately save the example to the topic.
          selectedExampleTexts.forEach((exampleText) => {
            const exampleAddedToCollection = this.examples.addExampleWithText(
              exampleText,
              this.locale.getSelectedLocale(),
            );
            exampleAddedToCollection.applyDefaults();
            //wait until the defaults are applied before closing the modal
            exampleAddedToCollection.onSynced(function () {
              //clean up entries
              exampleAddedToCollection.entries().prepareToSave();
              exampleAddedToCollection.saveModel();
            }, this);
          });
          const remainingExampleTexts = this.exampleGenerationModel
            .get("paraphrases")
            .filter((n) => !selectedExampleTexts.includes(n));
          this.exampleGenerationModel.set("paraphrases", remainingExampleTexts);
          Messenger.vent.trigger("close:modal");
        },
        this,
      );

      this.layout.on(
        "regenerate:examples",
        function () {
          this._generateAndShowExamples();
        },
        this,
      );

      this.layout.on(
        "cancel",
        function () {
          Messenger.vent.trigger("close:modal");
        },
        this,
      );

      //initialize sub modules
      this.layout.on(
        "show",
        function () {
          this._showSubViews(this.example, this.exampleGenerationModel);
        },
        this,
      );
      this.showRegion(this.layout);
    },
    _updateExampleTextCollectionArray: function () {
      this.exampleGenerationModel.exampleTextArray = this.examples.map(function (example) {
        // use lowercase version of each example text for case-insensitive comparison
        return example.getExampleText().toLowerCase();
      });
    },
    _generateAndShowExamples: function () {
      const utterance = this.example.getExampleText();
      Messenger.vent.trigger("track:interaction", {
        action: "Generate Examples",
        id: this.topicId,
        idType: "Topic",
        object: "Topic",
      });
      if (!utterance || utterance.trim().length === 0) {
        Messenger.okDialog($.t("topic.examples.generator.emptyUtteranceError"));
      } else {
        this.selectedLocale = this.locale.getSelectedLocale();
        // call the server endpoint to generate examples
        this.exampleGenerationModel.set("utterance", this.example.getExampleText());
        this.exampleGenerationModel.save(
          null,
          {
            error: () => {
              Messenger.vent.trigger("close:saving:display");
              Messenger.okDialog($.t("topic.examples.generator.generationError"));
            },
            localeId: this.selectedLocale,
          },
          this,
        );
        Messenger.vent.trigger("show:saving:display");
        this.exampleGenerationModel.onSynced(() => {
          Messenger.vent.trigger("close:saving:display");
          this._updateExampleTextCollectionArray();
        });
      }
    },
    _showSubViews: function (example, exampleGenerationModel) {
      this.gauge.show(example);
      const startingExampleString = example.getExampleText();
      this.locale.show(example);
      this.editor.show(example, startingExampleString);
      this.editor.hideTranslateButton(true);
      this.editor.hide(true);
      this.generatedExamplesList.show(exampleGenerationModel);
    },
  });
});
